import { FC, ReactNode } from 'react';
import { FastColor } from '@ant-design/fast-color';
import { Button, Divider, Dropdown, Flex, Menu } from 'antd';
import { TbKeyframe } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSubjectTags } from '@/queries';
import { SubjectReference, Tag } from '@/types';

import { DropdownMenuPanel } from '../../dropdowns/DropdownPanel';
import MiddleDot from '../../typography/MiddleDot';
import SmallTag from '../SmallTag';
import TagAssignmentDropdown from '../TagAssignmentDropdown';
import { createTagSearchHref, type TagSearchTab } from './utils';

interface ResourceHeaderTagsProps {
    disabled?: boolean;
    resourceLabel: string;
    showWhenEmpty?: boolean;
    subject: SubjectReference;
}

interface ResourceTagGroupProps {
    loading?: boolean;
    max?: number;
    min?: number;
    searchTab?: TagSearchTab;
    tags?: Tag[];
}

interface ResourceTagsDescriptionProps extends ResourceTagGroupProps {
    compact?: boolean;
    description?: ReactNode;
}

const TagColorDot: FC<{ color?: string | null }> = ({ color }) => {
    const { token } = useAntDesignTheme();

    return (
        <span
            aria-hidden
            style={{
                backgroundColor: color ?? token.colorTextQuaternary,
                borderRadius: '50%',
                flex: '0 0 auto',
                height: '0.375rem',
                marginRight: '0.25rem',
                width: '0.375rem',
            }}
        />
    );
};

const OverlappingTagKeyframes: FC<{ tags: Tag[] }> = ({ tags }) => {
    const { token } = useAntDesignTheme();
    const stackedTags = tags.slice(0, 3);
    const iconSize = 12;
    const stackOffset = 4;

    return (
        <span
            aria-hidden
            style={{
                display: 'inline-block',
                flex: '0 0 auto',
                height: `${iconSize}px`,
                marginRight: '0.25rem',
                position: 'relative',
                width: `${iconSize + Math.max(0, stackedTags.length - 1) * stackOffset}px`,
            }}
        >
            {stackedTags.map((tag, index) => {
                const color = new FastColor(tag.color ?? token.colorTextQuaternary)
                    .onBackground(token.colorBgContainer)
                    .setA(1)
                    .toHexString();

                return (
                    <TbKeyframe
                        aria-hidden
                        data-tag-keyframe='true'
                        fill={color}
                        fillOpacity={1}
                        key={tag.id}
                        opacity={1}
                        size={iconSize}
                        stroke={color}
                        strokeOpacity={1}
                        style={{
                            color,
                            fill: color,
                            left: `${index * stackOffset}px`,
                            opacity: 1,
                            position: 'absolute',
                            stroke: color,
                            top: 0,
                            zIndex: index + 1,
                        }}
                    />
                );
            })}
        </span>
    );
};

const ResourceTagGroup: FC<ResourceTagGroupProps> = ({
    loading = false,
    max = 3,
    min = 0,
    searchTab = 'workspace',
    tags = [],
}) => {
    if (loading) {
        return <SmallTag compact loading skeletonWidth='3rem' styles={{ marginLeft: 0 }} />;
    }

    const visibleLimit = Math.max(min, max);
    const visibleTags = tags.slice(0, visibleLimit);
    const hiddenTags = tags.slice(visibleLimit);

    if (!tags.length) {
        return null;
    }

    return (
        <Flex align='center' gap={0} style={{ minWidth: 0, overflow: 'hidden' }}>
            {visibleTags.map((tag, index) => (
                <Flex align='center' key={tag.id} style={{ flex: '0 1 auto', minWidth: 0 }}>
                    {index > 0 && <MiddleDot horizontalMargin='0.375rem' styles={{ flex: 'none' }} />}
                    <Link
                        aria-label={`Search with tag ${tag.name}`}
                        to={createTagSearchHref([tag.id], searchTab)}
                        style={{
                            color: 'inherit',
                            display: 'inline-flex',
                            flex: '0 1 auto',
                            minWidth: 0,
                            textDecoration: 'none',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <SmallTag
                            compact
                            icon={<TagColorDot color={tag.color} />}
                            message={tag.name}
                            styles={{
                                cursor: 'pointer',
                                display: 'inline-flex',
                                flex: '0 1 auto',
                                marginLeft: 0,
                                maxWidth: '10rem',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        />
                    </Link>
                </Flex>
            ))}
            {hiddenTags.length > 0 && (
                <>
                    {visibleTags.length > 0 && <MiddleDot horizontalMargin='0.375rem' styles={{ flex: 'none' }} />}
                    <Dropdown
                        arrow={false}
                        placement='bottomLeft'
                        trigger={['hover']}
                        popupRender={() => (
                            <DropdownMenuPanel selectable={false} style={{ minWidth: '12rem' }}>
                                <small style={{ display: 'block', padding: '0.25rem 0.75rem' }}>Additional Tags</small>
                                <Menu.Divider />
                                {hiddenTags.map((tag) => (
                                    <Menu.Item key={tag.id}>
                                        <Link
                                            aria-label={`Search with tag ${tag.name}`}
                                            to={createTagSearchHref([tag.id], searchTab)}
                                            style={{
                                                alignItems: 'center',
                                                color: 'inherit',
                                                display: 'flex',
                                                minWidth: 0,
                                                textDecoration: 'none',
                                                width: '100%',
                                            }}
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <TagColorDot color={tag.color} />
                                            <span
                                                style={{
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {tag.name}
                                            </span>
                                        </Link>
                                    </Menu.Item>
                                ))}
                            </DropdownMenuPanel>
                        )}
                    >
                        <Button
                            size='small'
                            style={{
                                background: 'transparent',
                                boxShadow: 'none',
                                height: '1.25rem',
                                lineHeight: 1,
                            }}
                            type='text'
                            onClick={(event) => event.stopPropagation()}
                        >
                            <SmallTag
                                compact
                                icon={<OverlappingTagKeyframes tags={hiddenTags} />}
                                message={`${hiddenTags.length}+ Tags`}
                                styles={{ cursor: 'pointer', marginLeft: 0, whiteSpace: 'nowrap' }}
                            />
                        </Button>
                    </Dropdown>
                </>
            )}
        </Flex>
    );
};

const ResourceTagsDescription: FC<ResourceTagsDescriptionProps> = ({
    compact = false,
    description,
    loading,
    max,
    min,
    searchTab,
    tags,
}) => {
    if (!loading && !tags?.length) {
        return compact ? (
            <span style={{ alignItems: 'center', display: 'inline-flex', height: '1.25rem', overflow: 'hidden' }}>
                {description}
            </span>
        ) : (
            <>{description}</>
        );
    }

    return (
        <Flex
            align='center'
            gap={0}
            style={{ height: compact ? '1.25rem' : undefined, minWidth: 0, overflow: 'hidden' }}
        >
            {description ? (
                <>
                    <span
                        style={{
                            flex: '0 1 auto',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {description}
                    </span>
                    <MiddleDot horizontalMargin='0.375rem' styles={{ flex: 'none' }} />
                </>
            ) : null}
            <ResourceTagGroup loading={loading} max={max} min={min} searchTab={searchTab} tags={tags} />
        </Flex>
    );
};

const ResourceHeaderTags: FC<ResourceHeaderTagsProps> = ({
    disabled,
    resourceLabel,
    showWhenEmpty = true,
    subject,
}) => {
    const { token } = useAntDesignTheme();
    const subjectTagsQuery = useSubjectTags(subject);
    const tags = subjectTagsQuery.data ?? [];

    if (subjectTagsQuery.isLoading) {
        return (
            <Flex align='center' data-resource-header-tags='true' style={{ minWidth: 0 }}>
                <Divider
                    data-resource-header-tags-divider='true'
                    style={{ borderColor: token.colorBorderSecondary, marginInline: token.marginXS }}
                    type='vertical'
                />
                <span style={{ display: 'inline-flex', minWidth: 0 }}>
                    <ResourceTagGroup loading />
                </span>
            </Flex>
        );
    }

    if (!showWhenEmpty && !tags.length) {
        return null;
    }

    return (
        <Flex align='center' data-resource-header-tags='true' style={{ lineHeight: 1, minWidth: 0 }}>
            <Divider
                data-resource-header-tags-divider='true'
                style={{ borderColor: token.colorBorderSecondary, marginInline: token.marginXS }}
                type='vertical'
            />
            <TagAssignmentDropdown disabled={disabled} resourceLabel={resourceLabel} subject={subject}>
                <button
                    aria-label={`Manage tags for ${resourceLabel}`}
                    disabled={disabled}
                    style={{
                        alignItems: 'center',
                        appearance: 'none',
                        background: 'transparent',
                        border: 0,
                        color: 'inherit',
                        display: 'inline-flex',
                        font: 'inherit',
                        margin: 0,
                        padding: 0,
                    }}
                    type='button'
                >
                    <SmallTag
                        compact
                        icon={
                            tags.length ? (
                                <OverlappingTagKeyframes tags={tags} />
                            ) : (
                                <TbKeyframe
                                    aria-hidden
                                    data-resource-tag-empty-icon='true'
                                    fill={token.colorTextTertiary}
                                    size={12}
                                    stroke={token.colorTextTertiary}
                                    style={{ display: 'block', marginRight: '0.25rem' }}
                                />
                            )
                        }
                        message={
                            tags.length
                                ? `${tags.length.toLocaleString()} ${tags.length === 1 ? 'Tag' : 'Tags'}`
                                : 'Add Tags'
                        }
                        styles={{ cursor: disabled ? 'default' : 'pointer', marginLeft: 0 }}
                    />
                </button>
            </TagAssignmentDropdown>
        </Flex>
    );
};

export { ResourceHeaderTags, ResourceTagGroup, ResourceTagsDescription };
